<template>
  <div class="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
    <div class="sm:mx-auto sm:w-full sm:max-w-md">
      <div class="text-center">
        <h1 class="text-3xl font-bold text-gray-900 mb-2">Portal de Facturación</h1>
        <p class="text-gray-600">Ingresa tu número de teléfono para comenzar</p>
      </div>
    </div>

    <div class="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
      <div class="bg-white py-8 px-4 shadow-xl rounded-lg sm:px-10">

        <!-- Paso 1: Ingresar teléfono -->
        <form v-if="paso === 1" @submit.prevent="solicitarOTP" class="space-y-6">
          <div>
            <label for="phone" class="block text-sm font-medium text-gray-700">Número de Teléfono</label>
            <div class="mt-1">
              <input id="phone" type="tel" v-model="phone" required maxlength="10"
                placeholder="10 dígitos, ej: 5512345678"
                class="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md placeholder-gray-400 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                :class="{ 'border-red-500': error }" />
            </div>
            <p v-if="error" class="mt-2 text-sm text-red-600">{{ errorMsg }}</p>
          </div>
          <button type="submit" :disabled="loading || phone.length < 10"
            class="w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed">
            <span v-if="loading" class="flex items-center">
              <svg class="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"><circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle><path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path></svg>
              Enviando...
            </span>
            <span v-else>Enviar Código</span>
          </button>
        </form>

        <!-- Paso 2: Ingresar código OTP -->
        <form v-if="paso === 2" @submit.prevent="verificarOTP" class="space-y-6">
          <div class="text-center mb-4">
            <p class="text-sm text-gray-600">Enviamos un código SMS al</p>
            <p class="text-lg font-semibold text-gray-900">{{ phone }}</p>
            <button type="button" @click="paso = 1; error = false" class="text-sm text-indigo-600 hover:text-indigo-500 mt-1">Cambiar número</button>
          </div>
          <div>
            <label for="code" class="block text-sm font-medium text-gray-700">Código de Verificación</label>
            <div class="mt-1">
              <input id="code" type="text" v-model="code" required maxlength="6"
                placeholder="6 dígitos"
                class="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md placeholder-gray-400 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm text-center text-2xl tracking-widest"
                :class="{ 'border-red-500': error }" />
            </div>
            <p v-if="error" class="mt-2 text-sm text-red-600">{{ errorMsg }}</p>
            <p class="mt-2 text-xs text-gray-500">El código expira en 5 minutos</p>
          </div>
          <button type="submit" :disabled="loading || code.length < 6"
            class="w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed">
            <span v-if="loading" class="flex items-center">
              <svg class="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"><circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle><path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path></svg>
              Verificando...
            </span>
            <span v-else>Verificar</span>
          </button>
          <div class="text-center">
            <button type="button" @click="reenviarOTP" :disabled="cooldown > 0"
              class="text-sm text-indigo-600 hover:text-indigo-500 disabled:text-gray-400 disabled:cursor-not-allowed">
              {{ cooldown > 0 ? `Reenviar en ${cooldown}s` : 'Reenviar código' }}
            </button>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script>
import facturacionCliente from '@/Services/facturacionCliente'
import { useClienteAuthStore } from '@/stores/clienteAuth'

export default {
  name: 'LoginOTP',
  data() {
    return {
      paso: 1,
      phone: '',
      code: '',
      loading: false,
      error: false,
      errorMsg: '',
      cooldown: 0,
      cooldownTimer: null
    }
  },
  mounted() {
    const store = useClienteAuthStore()
    if (store.isAuthenticated) {
      this.$router.push({ name: 'cliente-ordenes' })
    }
  },
  beforeUnmount() {
    if (this.cooldownTimer) clearInterval(this.cooldownTimer)
  },
  methods: {
    async solicitarOTP() {
      this.loading = true
      this.error = false
      try {
        const res = await facturacionCliente.solicitarOTP(this.phone)
        if (res.success) {
          this.paso = 2
          this.startCooldown()
        } else {
          this.error = true
          this.errorMsg = res.message
        }
      } catch (e) {
        this.error = true
        this.errorMsg = 'Error de conexión.'
      } finally {
        this.loading = false
      }
    },
    async verificarOTP() {
      this.loading = true
      this.error = false
      try {
        const res = await facturacionCliente.verificarOTP(this.phone, this.code)
        if (res.success) {
          const store = useClienteAuthStore()
          store.setAuth({ token: res.data.token, user: res.data.user })
          this.$router.push({ name: 'cliente-ordenes' })
        } else {
          this.error = true
          this.errorMsg = res.message
        }
      } catch (e) {
        this.error = true
        this.errorMsg = 'Error de conexión.'
      } finally {
        this.loading = false
      }
    },
    async reenviarOTP() {
      await this.solicitarOTP()
    },
    startCooldown() {
      this.cooldown = 60
      if (this.cooldownTimer) clearInterval(this.cooldownTimer)
      this.cooldownTimer = setInterval(() => {
        this.cooldown--
        if (this.cooldown <= 0) clearInterval(this.cooldownTimer)
      }, 1000)
    }
  }
}
</script>
